import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Package, Clock, MapPin, Phone, Mail, User, CheckCircle } from 'lucide-react';

const SharedOrder = () => {
  const { shareToken } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchOrder();
  }, [shareToken]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/orders/shared/${shareToken}`);
      setOrder(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching order:', error);
      setError('Order not found or link is invalid');
      setLoading(false);
    }
  };

  // Format date/time for order date - e.g., "Nov 16th, 1:56 PM"
  const formatOrderHeaderDateTime = (dateStr) => {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    
    // Add ordinal suffix (st, nd, rd, th)
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    const ordinalDay = `${day}${getOrdinalSuffix(day)}`;
    
    // Format time in 12-hour format with AM/PM
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    
    return `${month} ${ordinalDay}, ${hours}:${formattedMinutes} ${ampm}`;
  };

  // Format date with day name - e.g., "Nov 16th (Sun) at 12:15 PM"
  const formatOrderDateTime = (dateStr, timeStr) => {
    if (!dateStr) return timeStr || '';
    
    const date = new Date(dateStr);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const day = date.getDate();
    const dayName = dayNames[date.getDay()];
    const month = monthNames[date.getMonth()];
    
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    const ordinalDay = `${day}${getOrdinalSuffix(day)}`;
    
    const formatTime = (timeString) => {
      const parts = timeString.split(':');
      if (parts.length >= 2) {
        let hours = parseInt(parts[0]);
        const minutes = parseInt(parts[1]);
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        
        if (minutes === 0) {
          return `${hours} ${ampm}`;
        }
        return `${hours}:${minutes < 10 ? '0' + minutes : minutes} ${ampm}`;
      }
      return timeString;
    };
    
    let formattedTime = '';
    if (timeStr) {
      if (timeStr.includes('-')) {
        const [startTime, endTime] = timeStr.split('-').map(t => t.trim());
        const formattedStart = formatTime(startTime);
        const formattedEnd = formatTime(endTime);
        formattedTime = ` between ${formattedStart} - ${formattedEnd}`;
      } else {
        formattedTime = ` at ${formatTime(timeStr)}`;
      }
    }
    
    return `${month} ${ordinalDay} (${dayName})${formattedTime}`;
  };

  // Get pickup location display
  const getPickupLocationDisplay = (pickupLocation) => {
    if (!pickupLocation || pickupLocation === '') {
      return null;
    }
    
    // Check if it's an old order with just an index
    const index = parseInt(pickupLocation);
    if (!isNaN(index) && pickupLocation.length <= 2) {
      return `Location #${pickupLocation}`;
    }
    
    // Otherwise it's already formatted as "Name - Address"
    return pickupLocation;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-sm border border-red-200 max-w-md">
          <div className="text-center">
            <Package className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Order Not Found</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-5xl mx-auto px-4">
        {/* Main Card Container */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          
          {/* Header Section - Business Info Style */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Order Details</h1>
                <p className="text-xs text-gray-500 mt-0.5">Order #{order.order_number}</p>
              </div>
              <div className={`px-3 py-1.5 rounded-lg text-sm ${
                order.status === 'completed' ? 'bg-green-100 text-green-800' :
                order.status === 'in progress' ? 'bg-yellow-100 text-yellow-800' :
                order.status === 'received' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                <div className="flex items-center gap-1.5">
                  {order.status === 'completed' && <CheckCircle className="h-4 w-4" />}
                  <span className="font-medium capitalize">{order.status}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            
            {/* Customer & Order Info Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              
              {/* Customer Details */}
              <div>
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 -mx-6 mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">Customer Details</h3>
                </div>
                <div className="space-y-3 px-2">
                  <div className="flex items-center gap-2.5 text-sm">
                    <User className="h-4 w-4 text-theme-blue flex-shrink-0" />
                    <span className="text-gray-900 font-medium">
                      {typeof order.customer.name === 'object' && order.customer.name !== null
                        ? JSON.stringify(order.customer.name)
                        : order.customer.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm">
                    <Phone className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">
                      {typeof order.customer.phone === 'object' && order.customer.phone !== null
                        ? JSON.stringify(order.customer.phone)
                        : order.customer.phone}
                    </span>
                  </div>
                  {order.customer.email && (
                    <div className="flex items-center gap-2.5 text-sm">
                      <Mail className="h-4 w-4 text-purple-600 flex-shrink-0" />
                      <span className="text-gray-700 break-all">
                        {typeof order.customer.email === 'object' && order.customer.email !== null
                          ? JSON.stringify(order.customer.email)
                          : order.customer.email}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Details */}
              <div>
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 -mx-6 mb-4">
                  <h3 className="text-sm font-semibold text-gray-900">Order Details</h3>
                </div>
                <div className="space-y-2.5 px-2">
                  {/* Order Number */}
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="h-4 w-4 text-gray-500" />
                    <span className="text-gray-700">
                      <span className="font-medium">Order Number:</span> {order.order_number}
                    </span>
                  </div>

                  {/* Order Type */}
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="h-4 w-4 text-green-500" />
                    <span className="text-gray-700">
                      <span className="font-medium">Order Type:</span> {order.orderTypeLabel || (
                        order.orderType === 'orderNowPickup' ? 'Order Now - Pickup' :
                        order.orderType === 'preOrderPickup' ? 'Pre-Order - Pickup' :
                        order.orderType === 'orderNowDelivery' ? 'Order Now - Delivery' :
                        order.orderType === 'preOrderDelivery' ? 'Pre-Order - Delivery' :
                        order.orderType
                      )}
                    </span>
                  </div>

                  {/* Order Creation Date */}
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-theme-blue" />
                    <span className="text-gray-700">
                      <span className="font-medium">Order Date:</span> {formatOrderHeaderDateTime(order.orderDate)}
                    </span>
                  </div>

                  {/* Pickup/Delivery Time */}
                  {(order.pickupDate || order.deliveryDate) && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-purple-500" />
                      <span className="text-gray-700">
                        <span className="font-medium">
                          {order.orderType && order.orderType.includes('Pickup') ? 'Pickup Time:' : 
                           order.orderType && order.orderType.includes('Delivery') ? 'Delivery Time:' : 
                           'Scheduled Time:'}
                        </span> {formatOrderDateTime(
                          order.pickupDate || order.deliveryDate,
                          order.pickupTime || order.deliveryTime
                        )}
                      </span>
                    </div>
                  )}

                  {/* Pickup Location */}
                  {order.pickupLocation && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-orange-500 mt-0.5" />
                      <span className="text-gray-700">
                        <span className="font-medium">Pickup Location:</span> {getPickupLocationDisplay(order.pickupLocation)}
                      </span>
                    </div>
                  )}

                  {/* Delivery Address */}
                  {order.deliveryAddress && !order.pickupLocation && (
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="h-4 w-4 text-orange-500 mt-0.5" />
                      <span className="text-gray-700">
                        <span className="font-medium">Delivery Address:</span> {order.deliveryAddress}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order Items Section */}
            <div className="mb-6">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 -mx-6 mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Order Items</h3>
              </div>
              <div className="space-y-3">
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-start py-3 border-b border-gray-100 last:border-0">
                    <div className="flex-1 pr-4">
                      <p className="text-sm font-semibold text-gray-900">
                        {item.quantity}x{' '}
                        {typeof (item.name || item.product_name) === 'object'
                          ? JSON.stringify(item.name || item.product_name)
                          : (item.name || item.product_name)}
                      </p>
                      {item.variant && (
                        <p className="text-xs text-gray-600 mt-1">
                          <span className="font-medium">Variant:</span>{' '}
                          {typeof item.variant === 'object' && item.variant !== null
                            ? `${item.variant.name || item.variant.type} ${item.variant.price ? `($${parseFloat(item.variant.price).toFixed(2)})` : ''}`
                            : item.variant}
                        </p>
                      )}
                      {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                        <div className="text-xs text-gray-600 mt-1.5">
                          <span className="font-medium">Options:</span>
                          <div className="ml-2 mt-0.5 space-y-0.5">
                            {item.options && item.options.map(option => {
                              const selectedValue = item.selectedOptions[option.option_id];
                              if (!selectedValue) return null;
                              
                              // For single checkbox options
                              if (option.option_type === 'single' && selectedValue === true) {
                                return (
                                  <div key={option.option_id} className="text-gray-700">
                                    • {option.option_name}
                                    {option.price && parseFloat(option.price) > 0 && (
                                      <span className="text-gray-500"> (+${parseFloat(option.price).toFixed(2)})</span>
                                    )}
                                  </div>
                                );
                              }
                              
                              // For multiple choice options
                              if (option.option_type === 'multiple' && option.choices) {
                                const selectedChoice = option.choices.find(c => c.choice_id === selectedValue);
                                if (selectedChoice) {
                                  return (
                                    <div key={option.option_id} className="text-gray-700">
                                      • {option.option_name}: {selectedChoice.choice_name}
                                      {selectedChoice.price && parseFloat(selectedChoice.price) > 0 && (
                                        <span className="text-gray-500"> (+${parseFloat(selectedChoice.price).toFixed(2)})</span>
                                      )}
                                    </div>
                                  );
                                }
                              }
                              
                              return null;
                            })}
                            
                            {/* Fallback if options array is not available */}
                            {!item.options && Object.entries(item.selectedOptions).map(([key, value]) => {
                              const displayValue = typeof value === 'object' && value !== null
                                ? value.name || value.choice_name || value.option_name || JSON.stringify(value)
                                : (value === true ? key : value);
                              
                              // Skip if it's just a timestamp/ID
                              if (typeof displayValue === 'string' && /^\d+$/.test(displayValue)) {
                                return null;
                              }
                              
                              return (
                                <div key={key} className="text-gray-700">
                                  • {displayValue}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">${item.price.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary Section */}
            <div>
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 -mx-6 mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Order Summary</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal</span>
                  <span>${order.subtotal.toFixed(2)}</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-${order.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-700">
                  <span>Tax</span>
                  <span>${order.tax.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2">
                  <div className="flex justify-between text-base font-bold text-gray-900">
                    <span>Total</span>
                    <span>${order.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Special Instructions */}
            {order.specialInstructions && (
              <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 mt-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-1.5">Special Instructions</h4>
                <p className="text-sm text-gray-700">{order.specialInstructions}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedOrder;
